// VsDialpadPage.js — SEO landing page targeting "Dialpad alternative Australia"
// and "Dialpad Ai voice intelligence vs" queries. Public, no auth.
// Positions VoiceFabric as the coaching layer that KEEPS your Yabbit UCaaS,
// rather than the "rip and replace your phone system" story Dialpad requires.
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, DollarSign, Layers, Zap, ShieldCheck, Phone } from 'lucide-react';

const COMPARISON_ROWS = [
  { label: 'Per-user cost (AUD)', dialpad: 'A$124 – A$232 / user / mo', vf: 'A$49 – A$149 / user / mo', highlight: true },
  { label: 'Does it replace your phone system?', dialpad: 'Yes — Dialpad IS the UCaaS', vf: 'No — VoiceFabric sits on Yabbit UCaaS', highlight: true },
  { label: 'Roles graded', dialpad: 'Sales OR support (separate SKUs)', vf: 'Sales · Support · Admin · Accounts · Management (one SKU)' },
  { label: 'Setup fee', dialpad: 'Custom / project quote', vf: 'A$3,500 – A$11,900 fixed' },
  { label: 'Minimum seats', dialpad: 'Typically 10+ · annual contract', vf: '3 seats · A$299/mo min · month-to-month' },
  { label: 'Included minutes per user', dialpad: 'Bundled with phone plan', vf: '60 – 300 mins / user (grading + sentiment)' },
  { label: 'Overage pricing published', dialpad: 'Sales quote', vf: '$0.07/min transcription · $0.09/min sentiment' },
  { label: 'Australian data residency', dialpad: 'US-hosted default', vf: 'AU-hosted · tenant-isolated' },
  { label: 'Governance-independent escalation', dialpad: 'Score-based only', vf: 'Independent + score-based' },
  { label: 'White-label / reseller', dialpad: 'Not offered', vf: '50% reseller margin available' },
  { label: 'Time to deploy', dialpad: 'Full phone-system migration (weeks-months)', vf: '4 weeks · no phone-system change' },
];

const Chip = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ok ? 'bg-teal-500/15 text-teal-300' : 'bg-red-500/15 text-red-300'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {children}
  </span>
);

const VsDialpadPage = () => (
  <div className="min-h-screen bg-[#0A0F1C]" data-testid="vs-dialpad-page">
    {/* Header */}
    <header className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">Yabbit VoiceFabric</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/why-voicefabric" className="text-white/60 hover:text-white">Why VoiceFabric</Link>
          <Link to="/pricing" className="text-white/60 hover:text-white">Pricing</Link>
          <Link to="/login" className="text-teal-300 hover:text-teal-200 font-semibold" data-testid="vs-dialpad-signin">Sign in →</Link>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
      <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">Dialpad Ai · Alternative for Australian teams</p>
      <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
        Dialpad wants you to rip out your phone system.<br /><span className="text-teal-400">We just want to make it smarter.</span>
      </h1>
      <p className="text-white/70 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
        Dialpad Ai only works if you switch your entire UCaaS to Dialpad — often A$124–232/user/month all-in.
        Yabbit VoiceFabric adds role-specific grading, coaching and governance <strong className="text-white">on top of your existing Yabbit phone system</strong> from A$49/user/month.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-dialpad-pricing-cta"
        >
          See our pricing
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/free-audit"
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-dialpad-audit-cta"
        >
          Free 3-call audit
        </Link>
      </div>
    </section>

    {/* At-a-glance callouts */}
    <section className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-4">
      {[
        { icon: Phone, title: 'Keep your Yabbit UCaaS', body: 'No number porting, no line migration, no reseller conflict — VoiceFabric extends what you already run.' },
        { icon: DollarSign, title: 'Half the per-user cost', body: 'A$49–149/user/mo vs A$124–232/user/mo for Dialpad Support/Sell equivalents.' },
        { icon: Layers, title: 'One tenant, five roles', body: 'Dialpad splits sales and support into separate SKUs. VoiceFabric grades all 5 conversation types on one plan.' },
      ].map((c) => (
        <div key={c.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <c.icon className="w-6 h-6 text-teal-400 mb-3" />
          <h3 className="text-white font-bold text-lg">{c.title}</h3>
          <p className="text-white/60 text-sm mt-1">{c.body}</p>
        </div>
      ))}
    </section>

    {/* Comparison table */}
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="vs-dialpad-comparison">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Side-by-side · Dialpad Support / Sell vs VoiceFabric</h2>
        <p className="text-white/50 text-sm mt-3">Pricing as of Feb 2026, sourced from Dialpad&apos;s public pricing page + AU reseller quotes.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/60 text-xs uppercase tracking-wider bg-white/5">
              <th className="px-4 py-3 text-left">Feature</th>
              <th className="px-4 py-3 text-left">Dialpad Ai (Support / Sell)</th>
              <th className="px-4 py-3 text-left text-teal-300">Yabbit VoiceFabric</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {COMPARISON_ROWS.map((r) => (
              <tr key={r.label} className={r.highlight ? 'bg-teal-500/5' : ''}>
                <td className="px-4 py-3 text-white/80 font-medium">{r.label}</td>
                <td className="px-4 py-3 text-white/50"><Chip ok={false}>{r.dialpad}</Chip></td>
                <td className="px-4 py-3"><Chip ok>{r.vf}</Chip></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-white/30 text-[11px] mt-4 italic text-center">
        Dialpad figures from public pricing page (Support Advanced ≈ US$95/user/mo, Sell ≈ US$60–150/user/mo).
        AUD conversions at 1 USD ≈ 1.55 AUD. Feb 2026 snapshot — check dialpad.com for latest.
      </p>
    </section>

    {/* The three real reasons */}
    <section className="max-w-4xl mx-auto px-6 py-16 space-y-10">
      <div className="text-center mb-6">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Three reasons Yabbit customers stay on Yabbit</h2>
      </div>

      {[
        {
          n: '01',
          title: 'Switching UCaaS is a nine-month project — coaching is a two-week win',
          body: 'Every Dialpad conversation with an Australian SMB starts with "so we\'d migrate your numbers and lines". That\'s months of porting, line audits, faxback flows, reception retraining. VoiceFabric is a coaching layer over the phones you already own. Live in 4 weeks. Zero disruption to inbound calls.',
        },
        {
          n: '02',
          title: 'Dialpad grades sales OR support — never both on one bill',
          body: 'Dialpad Sell prices sales calls. Dialpad Support prices contact-centre calls. Admin, accounts, management? Not their product. VoiceFabric grades all five conversation types on the same tenant, same login, same knowledge base authoring, same coaching records — for one per-user price.',
        },
        {
          n: '03',
          title: 'Australian data + AU business-hours support, at half the price',
          body: 'Dialpad is a US-first platform priced in USD. VoiceFabric is AU-hosted, AU-invoiced, AU-supported, and priced for Australian SMB seat counts — from A$49/user/month with a 3-seat minimum. No US enterprise contract required.',
        },
      ].map((r) => (
        <div key={r.n} className="flex gap-6 items-start">
          <div className="text-6xl font-black text-teal-500/40 leading-none">{r.n}</div>
          <div>
            <h3 className="text-white font-bold text-xl mb-2">{r.title}</h3>
            <p className="text-white/70 leading-relaxed">{r.body}</p>
          </div>
        </div>
      ))}
    </section>

    {/* Honest — when Dialpad is the right pick */}
    <section className="max-w-3xl mx-auto px-6 py-12">
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
        <h3 className="text-amber-300 font-bold text-lg mb-3">Honest answer: when Dialpad is the right choice</h3>
        <p className="text-white/70 leading-relaxed">
          If you&apos;re <strong className="text-white">re-platforming your phone system anyway</strong>, want the whole stack from a single US vendor,
          and have the budget for A$150–230/user/month all-in, Dialpad is a strong choice — the AI is good, the UI is polished, and their sales team is world-class.
        </p>
        <p className="text-white/70 leading-relaxed mt-3">
          If you&apos;re <strong className="text-white">already on Yabbit</strong> and just want coaching, grading and governance <em>on top of</em> what you run today — without changing carriers, numbers or reception flows —
          VoiceFabric is a fraction of the total cost and a fraction of the deployment risk.
        </p>
      </div>
    </section>

    {/* FAQ */}
    <section className="max-w-3xl mx-auto px-6 py-16" data-testid="vs-dialpad-faq">
      <h2 className="text-3xl font-black text-white text-center mb-10">FAQ</h2>
      <div className="space-y-6">
        {[
          {
            q: 'Do we have to be on Yabbit UCaaS to use VoiceFabric?',
            a: 'Native integration is Yabbit — that\'s the 4-week deploy. On other UCaaS platforms (RingCentral, 8x8, Aircall, Dialpad itself) we support inbound via webhook integration on the Enterprise tier. But most customers arrive already on Yabbit or migrate to Yabbit + VoiceFabric together for the bundled economics.',
          },
          {
            q: 'How do the included minutes work?',
            a: 'Every plan bundles grading + sentiment minutes per user (Grade 60 / Coach 150 / Intelligence 300). Beyond your allowance, transcription is $0.07/min and sentiment is $0.09/min — published, no quote. Most SMB teams stay comfortably inside their bundle; heavy contact-centre volumes upgrade to Intelligence.',
          },
          {
            q: 'Is VoiceFabric a Dialpad or Aircall replacement?',
            a: 'No — those products replace your UCaaS. VoiceFabric is a coaching + intelligence layer on top of the UCaaS you already run. If you leave Yabbit, VoiceFabric can follow via our webhook integration; if you leave Dialpad, you\'d lose your phone system too.',
          },
          {
            q: 'What about privacy / Fair Work concerns?',
            a: 'Every call is already being recorded by your Yabbit UCaaS. VoiceFabric adds coaching to that recording — the rep sees their own dashboard, their own score, their own next focus. Coaching is FOR them, not surveillance OF them. We ship an "employee notify" template so HR can roll out transparently.',
          },
          {
            q: 'Can we try before we buy?',
            a: 'Two ways: (1) Free 3-call audit — upload three recordings, we grade them against your KB, you see the output before signing anything. (2) 14-day free trial on the Coach tier, no credit card required.',
          },
        ].map((f, i) => (
          <details key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 group">
            <summary className="cursor-pointer text-white font-semibold flex items-center justify-between">
              {f.q}
              <ArrowRight className="w-4 h-4 text-teal-400 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="max-w-4xl mx-auto px-6 py-16 text-center">
      <div className="bg-gradient-to-br from-teal-500/10 via-teal-500/5 to-transparent border border-teal-500/30 rounded-3xl p-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
          Keep the phone system that works.<br />Add the coaching layer that pays for itself.
        </h2>
        <p className="text-white/70 text-lg mt-4 max-w-xl mx-auto">
          Upload three of your recordings. We&apos;ll grade them against your knowledge base and show you exactly what Dialpad would charge you A$150+/user/month to see.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/free-audit"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold px-8 py-4 rounded-lg text-lg transition-colors"
            data-testid="vs-dialpad-final-cta"
          >
            Get my free audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold px-4 py-4"
          >
            See pricing →
          </Link>
        </div>
      </div>
    </section>

    {/* Trust bar */}
    <section className="max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-3 gap-8 text-center">
        <div>
          <ShieldCheck className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Australian data residency</h3>
          <p className="text-white/50 text-sm mt-1">AU-hosted · tenant-isolated · Privacy Act 2026 ready</p>
        </div>
        <div>
          <Zap className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Live in 4 weeks</h3>
          <p className="text-white/50 text-sm mt-1">Onboarding call · KB workshop · baseline scoring by week 3</p>
        </div>
        <div>
          <Layers className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">5-role grading</h3>
          <p className="text-white/50 text-sm mt-1">Sales, support, admin, accounts, management — one platform</p>
        </div>
      </div>
    </section>

    <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs">
      © 2026 Yabbit VoiceFabric · yabbit.com.au · <Link to="/pricing" className="hover:text-white/50">Pricing</Link> · <Link to="/why-voicefabric" className="hover:text-white/50">Why VoiceFabric</Link> · <Link to="/vs-aircall" className="hover:text-white/50">vs Aircall</Link>
    </footer>
  </div>
);

export default VsDialpadPage;
